import { ActivityStatus, ActivityUIKind } from "../../types";
import type { ActivityViewData } from "../../types";
import type { ActivityFirestore } from "../../ActivitiesContext";
import type { ActivityEventProvider, FirestoreActivityEvent } from "./types";

function toDate(ts: any): Date {
    return ts?.toDate?.() ?? new Date();
}

interface CIModelChangeEnrichment {
    workflowName?: string;
}

/**
 * Provider for activity events with kind "continuous_improvement_model_change".
 *
 * Title and description come directly from the event payload.
 * For events without a contextLabel in the payload, enrichment fetches the
 * workflow name from rapid_data → workflows to display as context.
 */
export const ciModelChangeProvider: ActivityEventProvider = {
    uiKind: ActivityUIKind.CIModelChange,

    convert(event: FirestoreActivityEvent, enrichment?: CIModelChangeEnrichment): ActivityViewData {
        return {
            id: event.id,
            kind: ActivityUIKind.CIModelChange,
            contextLabel:
                (event.payload?.contextLabel as string) || enrichment?.workflowName || undefined,
            title: (event.payload?.title as string) || "Model updated",
            description: (event.payload?.description as string) || null,
            status: ActivityStatus.Completed,
            icon: "far fa-sparkles",
            targetUrl:
                (event.payload?.targetUrl as string) ||
                `/redirect/id/${event.entity.id}?step=model`,
            openInNewTab: false,
            progress: null,
            error: null,
            createdAt: toDate(event.createdAt),
            completedAt: toDate(event.updatedAt)
        };
    },

    subscribeToEnrichment(
        event: FirestoreActivityEvent,
        onData: (data: CIModelChangeEnrichment) => void,
        firestore: ActivityFirestore
    ): () => void {
        // If the payload already has contextLabel, no enrichment needed
        if (event.payload?.contextLabel) {
            return () => {};
        }

        // Listen to rapid_data doc to get workflowId, then workflow doc for the name
        let unsubWorkflow: (() => void) | null = null;

        const unsubRapidData = firestore.onDocSnapshot(
            "rapid_data",
            event.entity.id,
            (doc) => {
                const workflowId = doc.data()?.workflowId as string | undefined;
                if (!workflowId) return;

                // Clean up previous workflow listener if workflowId changed
                unsubWorkflow?.();
                unsubWorkflow = firestore.onDocSnapshot(
                    "workflows",
                    workflowId,
                    (wfDoc) => {
                        const name = wfDoc.data()?.name as string | undefined;
                        if (name) {
                            onData({ workflowName: name });
                        }
                    },
                    () => {}
                );
            },
            () => {}
        );

        return () => {
            unsubRapidData();
            unsubWorkflow?.();
        };
    }
};
